import { AccountService } from './accountService';
import { UserService } from './userService';
import { Service, ServiceResponse } from './service';
import { PublicService } from './publicService';
import { NetworkService } from './networkService';
import { ForumsService } from './forumsService';
import { BlogsService } from './blogsService';
import { ActivityService } from './activityService';
import { MediaService } from './mediaService';
import { NotificationsService } from './notificationsService';
import { CommunityService } from './communityService';
import { Database } from './database';
import { ChatService } from './chatService';
import { MonitorService } from './monitorService';

export class Server {
  public static account: AccountService = new AccountService();
  public static user: UserService = new UserService();
  public static public: PublicService = new PublicService();
  public static forums: ForumsService = new ForumsService();
  public static blogs: BlogsService = new BlogsService();
  public static activity: ActivityService = new ActivityService();
  public static media: MediaService = new MediaService();
  public static network: NetworkService = new NetworkService();
  public static notifications: NotificationsService = new NotificationsService();
  public static community: CommunityService = new CommunityService();
  public static database: Database = new Database();
  public static chat: ChatService = new ChatService();
  public static monitor: MonitorService = new MonitorService();
  public static initialized: boolean = false;
  public static synchronizing: boolean = false;
  
  protected static signUpDetails: any = null;
  protected static communityId: string = null;
  protected static loggingIn: boolean = false;

  public static async init(community:string = null) {
    this.communityId = community;

    if(community) {
      Service.setCommunity(community);
      Server.network.setCommunity(community);
    }

    await Server.database.init();

    let promises = [];
    promises.push(Server.user.init());

    if(community) {
      promises.push(Server.notifications.init());
      promises.push(Server.activity.init());
      promises.push(Server.forums.init());
      promises.push(Server.public.init());
      promises.push(Server.chat.init());
      promises.push(Server.monitor.init());
    }

    let responses = await Promise.all(promises);

    for(let i = 0; i < responses.length; i++)
      if(!responses[i].success)
        return responses[i];

    Server.initialized = true;
  }

  public static getEnvironment() {
    return Service.getEnvironment();
  }

  public static async startSignUp(email: string, password: string): Promise<ServiceResponse> {
    let response = await Server.account.signUp(email, password);
    this.signUpDetails = response.success ? {email, password} : null;
    return response;
  }

  public static async completeSignUp(code: string): Promise<ServiceResponse> {
    let response = await Server.account.confirmRegistration(code);
    if(response.success)
      await this.login(this.signUpDetails.email, this.signUpDetails.password);
    return response;
  }

  public static isLoggingIn():boolean {
    return this.loggingIn;
  }

  public static async login(email: string, password: string): Promise<ServiceResponse> {
    Server.loggingIn = true;

    let response = await Server.account.authenticate(email, password);
    if(!response.success) {
      Server.loggingIn = false;
      return response;
    }

    let refreshToken = Server.account.getRefreshToken();
    window.localStorage.setItem('RefreshToken', refreshToken);
    window.localStorage.setItem('RefreshEmail', email);

    await Server.database.purgeAll();
    window.sessionStorage.clear();

    return Server.finishLogin();
  }

  public static hasAutoLogin(): boolean {
    let email = window.localStorage.getItem('RefreshEmail');
    let token = window.localStorage.getItem('RefreshToken');
    return (email != null && token != null);
  }

  public static async autoLogin(): Promise<ServiceResponse> {
    let email = window.localStorage.getItem('RefreshEmail');
    let token = window.localStorage.getItem('RefreshToken');

    if(!email || !token)
      return {success: false};
    
    Server.loggingIn = true;

    let response = await Server.account.authenticateWithToken(email, token);
    if(!response.success) {
      Server.loggingIn = false;
      return response;
    }

    return Server.finishLogin();
  }

  protected static async finishLogin() {
    Service.setAuthorizationToken(Server.account.getAuthorizationToken());

    let response = await Server.sync();
    if(!response.success) {
      await Server.signOut();
      Server.loggingIn = false;
      return response;
    }

    let community = Service.getCommunity();
    if(community != '') {
      Server.network.setCommunity(community);
      Server.network.connect();
    }
  
    Server.account.notifyListeners('login');

    this.monitor.start();

    Server.loggingIn = false;

    return {success: true};
  }

  public static async signOut() {
    this.monitor.stop();
    await Server.account.signOut();
    await Server.database.purgeAll();
    Server.user.clear();
    Server.network.disconnect();
    window.localStorage.removeItem('RefreshToken');
    window.localStorage.removeItem('RefreshEmail');
    window.sessionStorage.clear();
    Server.account.notifyListeners('logout');
    Service.setAuthorizationToken('');
  }

  public static async sync(): Promise<ServiceResponse> {
    Server.synchronizing = true;

    let response = await Server.user.sync();  
    if(!response.success) {
      Server.synchronizing = false;
      return response;
    }

    Server.synchronizing = false;

    return {success: true};
  }

  public static async refreshServiceLogin(): Promise<ServiceResponse> {
    let email = window.localStorage.getItem('RefreshEmail');
    let token = window.localStorage.getItem('RefreshToken');

    if(email && token) {
      let response = await Server.account.authenticateWithToken(email, token);
      if(!response.success)
        return response;

      console.log('Authentication token refreshed.');
        
      Service.setAuthorizationToken(Server.account.getAuthorizationToken());

      return response;
    }

    return {success: false};
  }

  // device notifications
  protected static async processNotifications(state:any) {
    // this.processingNotifications = true;

    // let count = state.notifications - this.lastNotifications;
    // let messages = Server.notifications.getMessages();
    
    // for(let i = 0; i < count; i++) {
    //   let nmess = '';
    //   let nstorage = '';

    //   switch(messages[i].type) {
    //     case 'post-like':
    //       nmess = 'liked your post!';
    //       nstorage = 'DeviceNotificationsLikes';
    //       break;
    //     case 'post-reply':
    //       nmess = 'replied to your post!';
    //       nstorage = 'DeviceNotificationsReplies';
    //       break;
    //     case 'post-reply-reply':
    //       nmess = 'replied to a post you also replied to!';
    //       nstorage = 'DeviceNotificationsReplies';
    //       break;
    //     case 're-post':
    //       nmess = 'reposted your activity!';
    //       nstorage = 'DeviceNotificationsReplies';
    //       break;
    //     case 'forum-post':
    //       nmess = 'posted in your forum topic!';
    //       nstorage = 'DeviceNotificationsReplies';
    //       break;
    //     case 'blog-comment':
    //       nmess = 'commented on your blog!';
    //       nstorage = 'DeviceNotificationsReplies';
    //       break;
    //     case 'friend-accept':
    //       break;
    //     case 'friend-decline':
    //       break;
    //     case 'friend-remove':
    //       break;
    //     case 'follow':
    //       break;
    //     case 'unfollow':
    //       break;
    //     case 'user-joined':
    //       break;
    //     case 'site-invite-accept':
    //       break;
    //   }

    //   if(nmess == '')
    //     continue;

    //   await Server.public.loadProfiles([messages[i].user]);
    //   let profile = Server.public.getProfile(messages[i].user);

    //   sendDeviceNotification(profile.name + ' ' + nmess, nstorage);
    // }

    // this.processingNotifications = false;
  }
}
